import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

function MarkdownRenderer({ markdown, className = '', invert = true }: Readonly<{ markdown: string; className?: string; invert?: boolean; }>) {

	const sanitizeSchema = {
		tagNames: [
			'p', 'br', 'hr', 'strong', 'b', 'em', 'i', 'u', 's', 'del', 'a', 'img',
			'ul', 'ol', 'li', 'input', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
			'blockquote', 'code', 'pre', 'table', 'thead', 'tbody', 'tr', 'th', 'td'
		],
		attributes: {
			span: ['style'],
			p: ['style'],
			li: ['style'],
			strong: ['style'],
			em: ['style'],
			u: ['style'],
			code: ['style'],
			a: ['href', 'target', 'rel'],
			img: ['src', 'alt', 'title', 'width', 'height', 'dataFilename', 'class'],
			input: ['type', 'checked', 'disabled']
		},
		protocols: {
			href: ['http', 'https', 'mailto'],
			src: ['http', 'https', 'data']
		}
	};

	return (
		<div className={`prose ${invert ? 'dark:prose-invert' : ''} max-w-none ${className}`}>
			<Markdown
				remarkPlugins={[remarkGfm]}
				rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
				components={{
					a: ({ ...props }) => (
						<a
							{...props}
							target="_blank"
							rel="noopener noreferrer"
							className="text-blue-600 underline bg-transparent"
						/>
					),
					img: ({ src, alt }) => {
						// Don't render images with empty or invalid src
						if (!src || src.trim() === '') {
							return null;
						}

						return (
							<img
								src={src}
								alt={alt || ''}
								className="max-w-full h-auto rounded-lg"
								loading="lazy"
							/>
						);
					},
					input: ({ ...props }) => (
						<input
							{...props}
							disabled
							className="mr-2"
						/>
					),
					ul: ({ ...props }) => (
						<ul
							{...props}
							className="list-disc list-inside ml-5 my-2"
						/>
					),
					ol: ({ ...props }) => (
						<ol
							{...props}
							className="list-decimal list-inside pl-5 my-2"
						/>
					),
					h1: ({ ...props }) => (
						<h1
							{...props}
							className="mt-5"
						/>
					),
					h2: ({ ...props }) => (
						<h2
							{...props}
							className="mt-5"
						/>
					),
					h3: ({ ...props }) => (
						<h3
							{...props}
							className="mt-5"
						/>
					),
					h4: ({ ...props }) => (
						<h4
							{...props}
							className="mt-5"
						/>
					),
					h5: ({ ...props }) => (
						<h5
							{...props}
							className="mt-5"
						/>
					),
					h6: ({ ...props }) => (
						<h6
							{...props}
							className="mt-5"
						/>
					),
					table: ({ ...props }) => (
						<table {...props} className="table-auto border border-collapse border-gray-400 my-3 w-full" />
					),
					thead: ({ ...props }) => (
						<thead {...props} className="bg-gray-100" />
					),
					tbody: ({ ...props }) => (
						<tbody {...props} />
					),
					tr: ({ ...props }) => (
						<tr {...props} className="border-b border-gray-400" />
					),
					th: ({ ...props }) => (
						<th {...props} className="text-left p-2 border border-gray-400" />
					),
					td: ({ ...props }) => (
						<td {...props} className="p-2 border border-gray-400" />
					),
					p: ({ ...props }) => (
						<p
							{...props}
							className="my-8"
						/>
					),
				}}
			>
				{markdown}
			</Markdown>
		</div>
	);
}

export default MarkdownRenderer;
